import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useForm } from "@inertiajs/react";
import axios, { AxiosRequestConfig } from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faChevronDown, faChevronUp, faCircleCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import Button from "@/Atoms/Input/Button";
import TextArea from "@/Atoms/Input/TextArea";
import TextInput from "@/Atoms/Input/TextInput/TextInput";
import InputError from "@/Components/InputError";
import InputLabel from "@/Components/InputLabel";
import Select, { ListOption } from "@/Atoms/Select/Select";
import ProductBox from "@/Molecules/ProductBox/ProductBox";
import PhotoUpload from "@/Organisms/MultiUpload/PhotoUpload";
import debounce from "@/helpers/debounce";
import { relativeRoute } from "@/helpers/relativeRoute";
import { Media } from "@/types/Media";
import { LandingProps } from "@/types/Landing";
import LandingPaperDetails from "@/Organisms/Landing/LandingPaperDetails";
import cardStyles from "@/Templates/Order/Input/InputSimple.module.scss";
import styles from "./LandingRequestForm.module.scss";

type RequestForm = {
  first_name: string
  last_name: string
  salutation: string
  email: string
  phone: string
  deceased_name: string
  user_person_relationship: string
  text: string
  files: Media[]
}

// The files are sent as attachments of one mail. Keep in sync with App\Services\OrderRequestService
const MAX_FILES = 5;
const MAX_FILES_SIZE_MB = 15;

// A pdf is uploaded as an image per page, all referring to the same original: it counts as one file
const fileKey = (file: any): string | undefined => file.original ?? file.path;

type PaperOption = LandingProps['otherPapers'][number];

const paperLabel = (option: PaperOption): ListOption => ({
  value: String(option.id),
  label: `${option.title} (${option.releases.toLowerCase()})`,
});

type TextField = 'first_name' | 'last_name' | 'email' | 'phone' | 'deceased_name';

/**
 * Request for a price and design, without further flow: it is handled in the dashboard under 'Aanvragen'.
 * The selected papers are kept by the page, so they can also be used to start the order flow.
 */
const LandingRequestForm = ({slug, paper, place, otherPapers, userPersonRelationships, selected, onToggle}: Pick<LandingProps, 'slug' | 'paper' | 'place' | 'otherPapers' | 'userPersonRelationships'> & {
  selected: number[]
  onToggle: (paperId: number) => void
}) => {
  const {data, setData, post, transform, processing, errors} = useForm<RequestForm>({
    first_name: '',
    last_name: '',
    salutation: '',
    email: '',
    phone: '',
    deceased_name: '',
    user_person_relationship: '',
    text: '',
    files: [],
  });
  const [sent, setSent] = useState(false);
  // The message is much smaller than the form it replaces: bring it into view
  const successRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (sent) {
      successRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
    }
  }, [sent]);
  const fieldErrors = errors as Record<string, string | undefined>;

  // The paper of the landing page is one of the choices, next to the other papers in the place or the area of the paper
  const papers: PaperOption[] = [{id: paper.id, title: paper.title, slug: paper.slug, releases: paper.releases, national: paper.national}, ...otherPapers];
  // Chosen papers found by the paper search, they are not in the list of papers in the place or the area of the paper
  const [searchedPapers, setSearchedPapers] = useState<PaperOption[]>([]);
  // Papers found by the paper search. Not kept in the state: rendering while typing resets the search input
  const foundRef = useRef<Record<number, PaperOption>>({});
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  // The papers in the place or the area of the paper are suggested, any other paper can be found by its title
  const suggestions = papers.filter((option) => !selected.includes(option.id)).map(paperLabel);
  const searchPapers = useRef(debounce((search: string) => new Promise<ListOption[]>(async (resolve) => {
    const term = search.trim().toLowerCase();
    const local = papers.filter((option) => option.title.toLowerCase().includes(term));
    let found: PaperOption[] = [];
    if (term.length >= 2) {
      try {
        found = (await axios.get(relativeRoute('seo.obituary.papers', {slug}), {params: {search: term}})).data;
      } catch (e) {
        // the suggested papers can still be chosen
      }
    }
    found.forEach((option) => foundRef.current[option.id] = option);
    resolve([...local, ...found.filter((option) => !local.some((paper) => paper.id === option.id))]
      .filter((option) => !selectedRef.current.includes(option.id))
      .map(paperLabel));
  }), 400, {leading: true})).current;
  // Papers of which the price, deadlines and distribution area are shown
  const [expanded, setExpanded] = useState<number[]>([]);
  const toggleExpanded = (paperId: number) => setExpanded((ids) => ids.includes(paperId)
    ? ids.filter((id) => id !== paperId)
    : [...ids, paperId]);
  const location = place ? 'seo_plaats' : 'seo_krant';
  const isUploading = data.files.some((file) => !(file as any).path);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    transform((data) => ({...data, papers: selected}));
    post(relativeRoute('seo.obituary.request', {slug}), {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => setSent(true),
    });
  };

  // Sizes of the uploaded files and the uploads in progress, to check the limits before uploading
  const filesRef = useRef(data.files);
  filesRef.current = data.files;
  const sizes = useRef<Record<string, number>>({});
  const pending = useRef({count: 0, size: 0});

  const handleFileUpload = async (file: File, id: string, index: number, uploadOptions: AxiosRequestConfig) => {
    const keys = [...new Set(filesRef.current.map(fileKey).filter(Boolean) as string[])];
    const size = keys.reduce((total, key) => total + (sizes.current[key] ?? 0), 0) + pending.current.size + file.size;
    if (keys.length + pending.current.count >= MAX_FILES) {
      throw new Error(`Je kunt maximaal ${MAX_FILES} bestanden meesturen.`);
    }
    if (size > MAX_FILES_SIZE_MB * 1024 * 1024) {
      throw new Error(`De bestanden zijn samen te groot. Je kunt maximaal ${MAX_FILES_SIZE_MB} MB meesturen.`);
    }

    const formData = new FormData();
    formData.append('file', file);

    pending.current = {count: pending.current.count + 1, size: pending.current.size + file.size};
    try {
      const response = await axios.post(relativeRoute('upload.tmp'), formData, uploadOptions);
      const key = fileKey(response.data.images?.[0] ?? {});
      if (key) {
        sizes.current[key] = file.size;
      }
      return response.data;
    } finally {
      pending.current = {count: pending.current.count - 1, size: pending.current.size - file.size};
    }
  };

  const handleFileChange = (media: Media[] | ((prev: Media[]) => Media[])) => {
    setData((prev) => ({...prev, files: typeof media === "function" ? media(prev.files) : media}));
  };

  const handleFileProgress = (percent: number, id: string) => {
    handleFileChange((files) => files.map((item) => item.id === id ? {...item, progress: percent} as Media : item));
  };

  const textField = (name: TextField, label: string, props: { type?: string, autoComplete?: string } = {}) => <div>
    <InputLabel htmlFor={`request_${name}`} value={label} required />
    <TextInput
      id={`request_${name}`}
      name={name}
      value={data[name]}
      onChange={(e: ChangeEvent<HTMLInputElement>) => setData(name, e.target.value)}
      fullWidth
      {...props}
    />
    <InputError message={errors[name]} className="mt-2" />
  </div>;

  // Checkable paper, with its price, deadlines and distribution area on demand
  const paperBox = (option: PaperOption) => <ProductBox
    key={option.id}
    title={option.title}
    subtitle={option.releases}
    selected={selected.includes(option.id)}
    aria-checked={selected.includes(option.id)}
    hideButton
    checkboxPosition={'left'}
    className={expanded.includes(option.id) ? styles.expanded : ''}
    onClick={() => onToggle(option.id)}
  >
    {expanded.includes(option.id) && <button
      type={'button'}
      className={styles.close}
      aria-label={'Minder tonen'}
      onClick={(e) => {
        e.stopPropagation();
        toggleExpanded(option.id);
      }}
    >
      <FontAwesomeIcon icon={faXmark} />
    </button>}
    <button
      type={'button'}
      className={styles.more}
      aria-expanded={expanded.includes(option.id)}
      onClick={(e) => {
        e.stopPropagation();
        toggleExpanded(option.id);
      }}
    >
      {expanded.includes(option.id) ? 'Minder tonen' : 'Tarieven, deadlines en verspreidingsgebied'}
      <FontAwesomeIcon icon={expanded.includes(option.id) ? faChevronUp : faChevronDown} className={'ml-2'} />
    </button>
    {/* clicks in the details (e.g. dragging the map) don't check the paper */}
    {expanded.includes(option.id) && <div className={'w-full'} onClick={(e) => e.stopPropagation()}>
      <LandingPaperDetails slug={option.slug} />
    </div>}
  </ProductBox>;

  const paperError = <InputError
    message={Object.entries(fieldErrors).find(([key]) => key.startsWith('papers'))?.[1]}
    className="mt-2"
  />;

  if (sent) {
    return <div className={cardStyles.card} id={'aanvraag'} ref={successRef}>
      <div className={`${cardStyles.body} ${styles.success}`} role={'status'}>
        <FontAwesomeIcon icon={faCircleCheck} size={'3x'} />
        <h2>We hebben je aanvraag ontvangen</h2>
        <p>Bedankt voor je aanvraag. We nemen zo snel mogelijk contact met je op.</p>
      </div>
    </div>;
  }

  return <form className={cardStyles.card} id={'aanvraag'} onSubmit={handleSubmit}>
    <div className={cardStyles.body}>
      {/* First of the two ways to continue, the other one (make it yourself) follows below the form */}
      <div className={styles.header}>
        <h2>Vraag vrijblijvend een prijs en ontwerp aan</h2>
        <p>
          Laat het ons weten via onderstaand formulier, dan nemen we contact met je op.
          Liever direct zelf aan de slag? <a href={'#zelf-maken'}>Maak zelf je rouwadvertentie</a>.
        </p>
      </div>

      {/* Entered by place: no paper is chosen up front. Entered by paper: that paper is already chosen. */}
      <div className={styles.section}>
        <div className={cardStyles.title}>
          <h2>Kies je krant(en)</h2>
          <p>
            {place
              ? <>Kies uit de kranten in {place.title} of zoek een andere krant waarin je de rouwadvertentie wilt plaatsen.</>
              : <>{paper.title} is al gekozen. Zoek eventueel andere kranten waarin je de rouwadvertentie ook wilt plaatsen.</>}
          </p>
        </div>
        <Select
          id={'request_paper_search'}
          isAsync
          cacheOptions={false} // chosen papers are left out of the results
          defaultOptions={suggestions}
          loadOptions={searchPapers}
          value={null} // Only used to add papers, the chosen papers are shown below
          placeholder={'Zoek op titel en selecteer een krant'}
          noOptionsMessage={() => 'Geen kranten gevonden'}
          loadingMessage={() => 'Bezig met zoeken...'}
          onChange={(option) => {
            if (!option) {
              return;
            }
            const id = Number(option.value);
            const found = foundRef.current[id];
            if (found && ![...papers, ...searchedPapers].some((paper) => paper.id === id)) {
              setSearchedPapers((searched) => [...searched, found]);
            }
            onToggle(id);
          }}
        />
        {paperError}
        {selected.length > 0 && <div className={styles.chosen}>
          <h3>Gekozen kranten</h3>
          <div className={styles.papers}>
            {/* in the order they were chosen */}
            {selected.map((id) => [...papers, ...searchedPapers].find((option) => option.id === id)).filter((option) => !!option).map(paperBox)}
          </div>
        </div>}
      </div>

      <div className={styles.section}>
        <div className={cardStyles.title}>
          <h2>Gegevens aanvrager</h2>
          <p>Via deze gegevens nemen we contact met je op.</p>
        </div>
        <div className={cardStyles.fields}>
          <div className={"grid grid-cols-1 md:grid-cols-2"}>
            {textField('first_name', 'Voornaam', {autoComplete: 'given-name'})}
            {textField('last_name', 'Achternaam', {autoComplete: 'family-name'})}
          </div>
          <div className={"grid grid-cols-1 md:grid-cols-2"}>
            <div>
              <InputLabel htmlFor="request_salutation" value="Aanhef" />
              <select
                id="request_salutation"
                value={data.salutation}
                onChange={(e) => setData('salutation', e.target.value)}
              >
                <option value=""></option>
                <option value="dhr">De heer</option>
                <option value="mevr">Mevrouw</option>
              </select>
              <InputError message={errors.salutation} className="mt-2" />
            </div>
          </div>
          <div className={"grid grid-cols-1 md:grid-cols-2"}>
            {textField('email', 'E-mailadres', {type: 'email', autoComplete: 'email'})}
            {textField('phone', 'Telefoonnummer', {type: 'tel', autoComplete: 'tel'})}
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <div className={cardStyles.title}>
          <h2>Gegevens overledene</h2>
          <p>Deze gegevens gebruiken we als basis voor de advertentie.</p>
        </div>
        <div className={cardStyles.fields}>
          <div className={"grid grid-cols-1 md:grid-cols-2"}>
            {textField('deceased_name', 'Naam overledene')}
            <div>
              <InputLabel htmlFor="request_user_person_relationship" value="Relatie tot overledene" required />
              <select
                id="request_user_person_relationship"
                value={data.user_person_relationship}
                onChange={(e) => setData('user_person_relationship', e.target.value)}
              >
                <option value=""></option>
                {userPersonRelationships.map((relationship) => <option key={relationship.value} value={relationship.value}>
                  {relationship.label}
                </option>)}
              </select>
              <InputError message={errors.user_person_relationship} className="mt-2" />
            </div>
          </div>
          <div>
            <InputLabel htmlFor="request_text" value="Tekst van de rouwadvertentie of toelichting" />
            <TextArea
              id="request_text"
              rows={6}
              maxLength={10000}
              value={data.text}
              onChange={(e) => setData('text', e.target.value)}
            />
            <InputError message={errors.text} className="mt-2" />
          </div>
          <div>
            <InputLabel value="Bestanden" />
            <PhotoUpload
              items={data.files}
              onChange={handleFileChange}
              onProgress={handleFileProgress}
              onUpload={handleFileUpload}
              limitLabel={`maximaal ${MAX_FILES} bestanden, samen ${MAX_FILES_SIZE_MB} MB`}
              acceptedFileTypes={{
                'image/jpeg': [],
                'image/png': [],
                'application/pdf': [],
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
                'application/msword': ['.doc'],
              }}
            />
            <InputError
              message={Object.entries(fieldErrors).find(([key]) => key.startsWith('files'))?.[1]}
              className="mt-2"
            />
          </div>
        </div>
      </div>
    </div>

    <div className={cardStyles.footer}>
      <p>Velden met een <span className={'color-error font-bold'}>*</span> zijn verplicht. Alle overige velden zijn
        optioneel.</p>
      <div className={cardStyles.actions}>
        <Button
          type={'submit'}
          isLoading={processing}
          disabled={isUploading || processing}
          endIcon={faArrowRight}
          data-sa-event="verstuur_seo_aanvraag" data-sa-location={location}
        >
          Aanvraag versturen
        </Button>
      </div>
    </div>
  </form>;
};

export default LandingRequestForm;
